import React from "react";

const ScaleLabel = ({ label, fallback, align = "left" }) => {
  if (!label) return <span className="text-sm font-semibold text-spiders-text-secondary">{fallback}</span>;

  const mainPart = label.split("(")[0].replace(/^predominantly\s*/i, "").trim();
  const qualifier = label.match(/\(([^)]+)\)/)?.[1];

  return (
    <div className={align === "right" ? "text-right" : "text-left"}>
      <span className="text-sm font-semibold text-spiders-text-secondary block">{mainPart}</span>
      {qualifier && (
        <span className="text-[11px] text-spiders-text-muted leading-snug block mt-0.5">{qualifier}</span>
      )}
    </div>
  );
};

const SliderQuestion = ({ question, value, onChange }) => {
  const {
    questionId,
    min = 1,
    max = 7,
    step = 1,
    minLabel = "",
    maxLabel = "",
    centerLabel = "",
    hideValue = false,
    startCenter = false,
  } = question;

  const centerValue = Math.round((min + max) / 2);
  const currentValue = value ?? (startCenter ? centerValue : Math.round((min + max) / 2));
  const hasValue = value !== undefined && value !== null;

  // Generate tick marks — for large ranges, use major ticks every 10
  const range = max - min;
  const tickStep = range > 20 ? 10 : step;
  const ticks = [];
  for (let i = min; i <= max; i += tickStep) {
    ticks.push(i);
  }

  return (
    <div className="px-1">
      {/* Labels above the slider — bold label + subtle qualifier */}
      <div className="flex items-start justify-between mb-3 gap-6">
        <div className="flex-1">
          <ScaleLabel label={minLabel} fallback={min} />
        </div>
        {centerLabel && (
          <div className="flex-shrink-0 text-center pt-0.5">
            <span className="text-xs text-spiders-text-muted">{centerLabel}</span>
          </div>
        )}
        <div className="flex-1">
          <ScaleLabel label={maxLabel} fallback={max} align="right" />
        </div>
      </div>

      {/* Tick marks */}
      <div className="relative w-full h-0 mb-1 px-[2px]">
        <div className="absolute inset-x-0 flex justify-between px-[2px]">
          {ticks.map((tick) => (
            <div
              key={tick}
              className={`w-px h-2 ${
                hasValue && tick === currentValue
                  ? "bg-transparent"
                  : "bg-spiders-text-muted/30"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Slider input */}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={currentValue}
        onChange={(e) => onChange(questionId, Number(e.target.value))}
        className={`w-full h-2 rounded-lg appearance-none cursor-pointer transition-all
          ${hasValue ? "bg-spiders-primary-500/30" : "bg-spiders-bg-elevated"}
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:w-5
          [&::-webkit-slider-thumb]:h-5
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:shadow-lg
          [&::-webkit-slider-thumb]:transition-transform
          [&::-webkit-slider-thumb]:hover:scale-110
          ${hasValue
            ? "[&::-webkit-slider-thumb]:bg-spiders-primary-500"
            : "[&::-webkit-slider-thumb]:bg-spiders-text-muted/50 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-dashed [&::-webkit-slider-thumb]:border-spiders-text-muted"
          }
          [&::-moz-range-thumb]:appearance-none
          [&::-moz-range-thumb]:w-5
          [&::-moz-range-thumb]:h-5
          [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:shadow-lg
          [&::-moz-range-thumb]:border-none
          ${hasValue
            ? "[&::-moz-range-thumb]:bg-spiders-primary-500"
            : "[&::-moz-range-thumb]:bg-spiders-text-muted/50 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-dashed [&::-moz-range-thumb]:border-spiders-text-muted"
          }`}
      />

      {/* Numeric scale labels below */}
      <div className="flex justify-between mt-1 px-[2px]">
        {ticks.map((tick) => (
          <span
            key={tick}
            className={`text-[10px] w-4 text-center ${
              hasValue && tick === currentValue
                ? "text-spiders-primary-400 font-bold"
                : "text-spiders-text-muted/50"
            }`}
          >
            {tick}
          </span>
        ))}
      </div>

      {/* Selection feedback */}
      {!hasValue ? (
        <p className="text-center text-spiders-text-muted text-xs italic mt-2">
          Drag to select
        </p>
      ) : range > 20 && (
        <p className="text-center text-spiders-primary-400 text-sm font-semibold mt-1">
          {currentValue}
        </p>
      )}
    </div>
  );
};

export default SliderQuestion;
